/**
 * Version staleness monitor.
 *
 * Polls `/api/version` every 2 minutes once the app is mounted. Compares
 * the server's live-deployed version against this bundle's compile-time
 * `APP_VERSION`. When they diverge, the user's browser is running an
 * older JS bundle (typically because we just deployed a new release and
 * their tab has been open for a while).
 *
 * Consumers:
 *   - `<StaleVersionBanner>` at the top of every authed page
 *   - `<Sidebar>`'s Version pill (green when current, amber when stale)
 *
 * The hook is DOM-safe: it gracefully handles being called outside of
 * the provider (returns `isStale=false, live=null`) so we never crash
 * on the login screen or any other page that mounts before the
 * provider wraps the tree.
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { APP_VERSION } from "@/version";
import api from "@/lib/api";

const POLL_INTERVAL_MS = 2 * 60 * 1000;       // every 2 minutes
const INITIAL_DELAY_MS = 15 * 1000;           // wait 15s after mount before first poll

const VersionContext = createContext({
  isStale: false,
  live: null,
  bundleVersion: APP_VERSION,
  dismissedForSession: false,
  dismiss: () => {},
  checkNow: async () => {},
});

export function VersionProvider({ children }) {
  const [live, setLive] = useState(null);           // {version, release_date, deployed_at}
  const [dismissed, setDismissed] = useState(false);

  const check = useCallback(async () => {
    try {
      const res = await api.get("/version", { /* unauthenticated — base url handles /api prefix */ });
      if (res?.data?.version) {
        setLive(res.data);
        // If the live version is NOT the one we booted with, a new
        // bundle was pushed. Surface the dismiss UI again on the NEXT
        // version change (reset dismissed when the live version itself
        // moves a second time).
        if (res.data.version !== APP_VERSION) {
          // Keep dismissed-state — user will re-see the banner on
          // another release if they haven't refreshed yet, via the
          // effect below that clears dismissed when `live.version`
          // transitions.
        }
      }
    } catch {
      // Silent — the banner just stays hidden if the endpoint is
      // unreachable (e.g. backend deploy in progress).
    }
  }, []);

  useEffect(() => {
    // Initial poll after a short delay so we don't race against the
    // user's first page load.
    const t0 = setTimeout(check, INITIAL_DELAY_MS);
    const t1 = setInterval(check, POLL_INTERVAL_MS);
    return () => { clearTimeout(t0); clearInterval(t1); };
  }, [check]);

  // If the live version bumps AGAIN after the user has dismissed the
  // banner, clear the dismissal so they see the new prompt.
  useEffect(() => {
    if (live?.version && live.version !== APP_VERSION) {
      // Fresh stale state — only reset if the live version has changed
      // since last dismissal. Store the dismissed-for-version in
      // sessionStorage so a refresh (before they hard-refresh) doesn't
      // silently suppress the banner for the new version.
      try {
        const dismissedFor = sessionStorage.getItem("eyvo_stale_dismissed_for");
        if (dismissedFor && dismissedFor !== live.version) {
          sessionStorage.removeItem("eyvo_stale_dismissed_for");
          setDismissed(false);
        } else if (dismissedFor === live.version) {
          setDismissed(true);
        }
      } catch { /* sessionStorage may be blocked */ }
    }
  }, [live?.version]);

  // Version-comparison: only flag the bundle as stale when the SERVER's
  // version is genuinely *newer* than the one this browser loaded.
  // Previously we did a plain `!==` which fired the "refresh!" banner
  // every time the bundle drifted in either direction — including the
  // few seconds during a deploy when the backend has been restarted
  // but the frontend hasn't, leading to confusing messages like
  // "new version 1.14.2 available" (older!) when the browser was
  // already on 1.15. Semantic compare fixes that.
  const isStale = (() => {
    if (!live?.version) return false;
    if (live.version === APP_VERSION) return false;
    const parse = (v) => String(v).split(".").map((s) => parseInt(s, 10) || 0);
    const a = parse(APP_VERSION);  // browser bundle
    const b = parse(live.version); // server
    const len = Math.max(a.length, b.length);
    for (let i = 0; i < len; i++) {
      const av = a[i] || 0, bv = b[i] || 0;
      if (bv > av) return true;   // server is newer → stale (refresh)
      if (bv < av) return false;  // server is older → mid-deploy, no banner
    }
    return false;  // exactly equal after parse (e.g. "1.15" vs "1.15.0")
  })();

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      if (live?.version) {
        sessionStorage.setItem("eyvo_stale_dismissed_for", live.version);
      }
    } catch { /* ignore */ }
  }, [live?.version]);

  const value = {
    isStale,
    live,
    bundleVersion: APP_VERSION,
    dismissedForSession: dismissed,
    dismiss,
    checkNow: check,
  };
  return <VersionContext.Provider value={value}>{children}</VersionContext.Provider>;
}

export function useVersion() {
  return useContext(VersionContext);
}
